import { Drawer } from "antd"
import { XMarkIcon } from "@heroicons/react/24/outline"

import { Button } from "components"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { Person } from "./interface/confirmation"

const ConfirmationReviewDrawer = ({
  open,
  people,
  isConfirming,
  onClose,
  onConfirm,
}: {
  open: boolean
  people: Person[]
  isConfirming: boolean
  onClose: () => void
  onConfirm: () => void
}) => {
  const count = people.length

  return (
    <Drawer
      width={580}
      open={open}
      onClose={onClose}
      closeIcon={null}
      title={
        <div className="font-avenir flex justify-between items-start gap-2">
          <div>
            <p className="text-lg text-[#0C1B3D] font-bold">
              Employee confirmation
            </p>
            <p className="text-sm text-neutral100 font-light">
              You&apos;re about to confirm {count}{" "}
              {count === 1 ? "employee" : "employees"}
            </p>
          </div>
          <XMarkIcon
            className="text-[#111827] w-5 shrink-0"
            role="button"
            onClick={onClose}
          />
        </div>
      }
      footer={
        <div className="w-full flex justify-end items-center gap-4">
          <Button
            color="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={onConfirm}
            submitting={isConfirming}
            disabled={isConfirming || count === 0}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="font-avenir text-[#42526D]">
        <p className="text-sm text-neutral500">
          Once you confirm these employees, their profiles will be updated in
          the system, ensuring seamless access to company resources and
          privileges.
        </p>

        {count === 0 ? (
          <p className="mt-6 text-sm text-neutral200">
            No employees selected.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-neutral30">
            {people.map((person) => (
              <li
                key={person.person_id}
                className="flex items-start gap-3 py-3"
              >
                <PersonAvatar
                  className="w-8 h-8 rounded-full shrink-0"
                  src={person.avatar_url}
                  alt=""
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="uppercase font-semibold text-neutral500">
                      {person.first_name}
                    </span>{" "}
                    {person.middle_name ? `${person.middle_name} ` : ""}
                    {person.last_name}
                  </p>
                  {person.email ? (
                    <p className="text-sm text-neutral400 truncate">
                      {person.email}
                    </p>
                  ) : null}
                  <p className="text-[11px] !text-neutral200 font-light">
                    {person.department}
                    {person.employee_title ? ` · ${person.employee_title}` : ""}
                  </p>
                </div>
                <span className="text-xs text-neutral200 shrink-0">
                  {person.confirmation_days_late} days late
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  )
}

export default ConfirmationReviewDrawer
